import { useCallback, useEffect, useState } from 'react';
import type { MeDto } from '@bb/shared';
import { api, ApiError } from '../net/api';
import { Welcome } from './Welcome';
import { Creator } from './Creator';
import { Play } from './Play';
import { PolicyView } from './Policy';
import { EmailLinkPage, ResetPasswordPage, VerifyEmailPage } from './EmailPages';
import { FamilyPage, ParentConsentPage } from './Family';
import { BetaPage, isBetaHost } from './BetaPage';
import { FormPage, NEXT_KEY } from './FormPage';

type Screen =
  | { kind: 'loading' }
  | { kind: 'welcome'; notice?: string }
  | { kind: 'closed'; message: string }
  | { kind: 'creator'; me: MeDto; extra?: boolean }
  | { kind: 'play'; me: MeDto };

export function App() {
  if (isBetaHost() || /^\/beta\/?$/.test(window.location.pathname)) return <BetaPage />;
  const form = /^\/forms\/([a-z0-9-]+)\/?$/.exec(window.location.pathname);
  if (form) return <FormPage slug={form[1]!} />;
  const policy = /^\/policies\/([a-z0-9-]+)\/?$/.exec(window.location.pathname);
  if (policy) return <PolicyView slug={policy[1]!} />;
  const token = new URLSearchParams(window.location.search).get('token') ?? '';
  if (window.location.pathname === '/verify-email') return <VerifyEmailPage token={token} />;
  if (window.location.pathname === '/reset-password') return <ResetPasswordPage token={token} />;
  if (window.location.pathname === '/change-email') return <EmailLinkPage token={token} kind="confirm" />;
  if (window.location.pathname === '/undo-email') return <EmailLinkPage token={token} kind="undo" />;
  if (window.location.pathname === '/parent-consent') return <ParentConsentPage token={token} />;
  if (window.location.pathname === '/family') return <FamilyPage />;
  return <Game />;
}

function Game() {
  const [screen, setScreen] = useState<Screen>({ kind: 'loading' });

  const route = useCallback((me: MeDto) => {
    // Back to the form that asked them to sign in.
    let next: string | null = null;
    try {
      next = sessionStorage.getItem(NEXT_KEY);
      sessionStorage.removeItem(NEXT_KEY);
    } catch {
      /* storage blocked */
    }
    if (next && /^\/forms\/[a-z0-9-]+$/.test(next)) {
      window.location.assign(next);
      return;
    }
    setScreen(me.character ? { kind: 'play', me } : { kind: 'creator', me });
  }, []);

  const refresh = useCallback(async () => {
    try {
      route(await api.me());
    } catch (e) {
      if (e instanceof ApiError && e.code === 'login_closed') setScreen({ kind: 'closed', message: e.message });
      else if (e instanceof ApiError && (e.status === 401 || e.status === 403)) setScreen({ kind: 'welcome', notice: e.status === 403 ? e.message : undefined });
      else setScreen({ kind: 'closed', message: 'We could not reach Buddy Bay. Check your connection and refresh.' });
    }
  }, [route]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signOut = async () => {
    await api.logout().catch(() => {});
    setScreen({ kind: 'welcome' });
  };

  switch (screen.kind) {
    case 'loading':
      return <div className="center-screen"><p className="muted">Loading Buddy Bay…</p></div>;
    case 'closed':
      return (
        <div className="center-screen">
          <div className="panel narrow">
            <h1 className="brand">Buddy Bay</h1>
            <p>{screen.message}</p>
            <button className="btn" onClick={() => void refresh()}>Try again</button>
          </div>
        </div>
      );
    case 'welcome':
      return <Welcome notice={screen.notice} onSignedIn={route} onClosed={(m) => setScreen({ kind: 'closed', message: m })} />;
    case 'creator':
      return (
        <Creator
          me={screen.me}
          // A second or third character: reload so the world, outfit and name all start fresh.
          onCreated={(character) => (screen.extra ? window.location.reload() : setScreen({ kind: 'play', me: { ...screen.me, character } }))}
          onSignOut={signOut}
          onCancel={screen.extra ? () => setScreen({ kind: 'play', me: screen.me }) : undefined}
        />
      );
    case 'play':
      return <Play me={screen.me} onSignOut={signOut} onReconnect={() => window.location.reload()} onNewCharacter={() => setScreen({ kind: 'creator', me: screen.me, extra: true })} />;
  }
}
